import { LoadingButton } from '@mui/lab';
import {
  type Breakpoint,
  Button,
  type ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import { CloseIcon } from 'components/icons/CloseIcon';
import type { FC, ReactNode } from 'react';
import IconButtonAnimate from './animate/IconButtonAnimate';

interface Props {
  open?: boolean;
  handleClose: () => void;
  title: string;
  description: string | ReactNode;
  cancelButton?: Omit<ButtonProps, 'children' | 'onClick' | 'action'> & {
    label: string | ReactNode;
    action?: () => void;
  };
  actionButton: Omit<ButtonProps, 'children' | 'onClick' | 'action'> & {
    label: string | ReactNode;
    isLoading?: boolean;
    action?: () => void;
  };
  canExtendToFullWidth?: boolean;
  maxWidth?: Breakpoint;
  shouldDisplayCloseButton?: boolean;
}

export const SimpleDialogBox = ({
  open = true,
  handleClose,
  title,
  description,
  cancelButton,
  actionButton,
  canExtendToFullWidth,
  maxWidth,
  shouldDisplayCloseButton,
}: Props) => {
  return (
    <Dialog open={open} onClose={handleClose} maxWidth={maxWidth} fullWidth={canExtendToFullWidth}>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between">
          {title}
          {shouldDisplayCloseButton && (
            <IconButtonAnimate>
              <CloseIcon
                sx={{ cursor: 'pointer', color: 'gray' }}
                width={20}
                height={20}
                onClick={handleClose}
              />
            </IconButtonAnimate>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        {typeof description === 'string' ? (
          <DialogContentText>{description}</DialogContentText>
        ) : (
          description
        )}
      </DialogContent>
      <DialogActions>
        {cancelButton && <CancelButton {...cancelButton} />}
        {actionButton && <ActionButton {...actionButton} />}
      </DialogActions>
    </Dialog>
  );
};

const CancelButton: FC<NonNullable<Props['cancelButton']>> = (props) => {
  const { label, action, ...rest } = props;

  return (
    <Button onClick={action} {...rest}>
      {label}
    </Button>
  );
};

const ActionButton: FC<NonNullable<Props['actionButton']>> = (props) => {
  const { label, action, isLoading, ...rest } = props;

  return (
    <LoadingButton
      autoFocus
      loading={isLoading}
      type="button"
      variant="contained"
      onClick={action}
      {...rest}
    >
      {label}
    </LoadingButton>
  );
};
